/**
 * Ordered tour step definitions for the OMEN Web Tracker guided tour.
 * Uses shepherd.js v15 StepOptions. All DOM targeting is done via data-tour
 * attributes or well-known MapLibre GL CSS classes for native controls.
 *
 * @param inject  - injects mock data into the Zustand store
 * @param cleanup - removes mock data from the store (called on cancel only)
 */

import type { StepOptions, StepOptionsButton } from 'shepherd.js';
import { useAppStore } from '../store/appStore';
import { MOCK_FLIGHT_ACTIVE, MOCK_FLIGHT_ENDED } from './mockTourData';

const TOUR_FLIGHT_ENDED_ID = MOCK_FLIGHT_ENDED.id;
const TOUR_FLIGHT_ACTIVE_ID = MOCK_FLIGHT_ACTIVE.id;

/** Shared "Précédent" button — goes to the previous step. */
const prevBtn: StepOptionsButton = {
  text: '← Précédent',
  action(): void { this.back(); },
  classes: 'shepherd-button-secondary',
};

/** Shared "Suivant" button — advances to the next step. */
const nextBtn: StepOptionsButton = {
  text: 'Suivant →',
  action(): void { this.next(); },
};

/**
 * "Terminer" button — completes the tour.
 * Does NOT call cleanup(): mock data stays alive so the user can explore freely.
 */
const terminerBtn: StepOptionsButton = {
  text: 'Terminer',
  action(): void { this.complete(); },
};

/** Opens a modal by clicking its trigger button if the modal is not already in the DOM. */
function openModalIfNeeded(
  modalAttr: string,
  triggerAttr: string,
  delay = 300,
): Promise<void> {
  return new Promise((resolve) => {
    if (document.querySelector(`[data-tour="${modalAttr}"]`)) {
      resolve();
      return;
    }
    document.querySelector<HTMLButtonElement>(`[data-tour="${triggerAttr}"]`)?.click();
    setTimeout(resolve, delay);
  });
}

/**
 * Closes the flight selection modal by clicking its "Annuler" button.
 * Uses the data-tour attribute added to the cancel button (Fix 7).
 */
function closeSelectionModal(): void {
  document.querySelector<HTMLButtonElement>('[data-tour="tour-flight-list-panel-close"]')?.click();
}

/**
 * Closes the settings modal by clicking its ✕ close button.
 * Uses data-tour="tour-settings-close-button" (Fix 6).
 * More reliable than clicking the overlay (which would require multi-level DOM traversal).
 */
function closeSettingsModal(): void {
  document.querySelector<HTMLButtonElement>('[data-tour="tour-settings-close-button"]')?.click();
}

/**
 * Closes both the flight selection modal and the settings modal.
 * Called on all tour exit paths (complete, cancel, quit, unmount) from TourDemoRoute.
 */
export function closeTourModals(): void {
  closeSelectionModal();
  closeSettingsModal();
}

/**
 * Builds the complete ordered array of Shepherd step options.
 * @param inject  - mock data injection callback
 * @param cleanup - mock data removal callback (cancel only)
 */
export function createTourSteps(
  inject: () => void,
  cleanup: () => void,
): StepOptions[] {
  void cleanup; // cleanup is registered on the tour's cancel event in TourDemoRoute
  const store = () => useAppStore.getState();

  return [
    // ── STEP 0 — Introduction (centered, no attachment) ───────────────────────
    {
      id: 'intro',
      title: '🎈 Bienvenue dans OMEN',
      text: `
        <p>Cette visite guidée vous présente les principales fonctionnalités du
        <strong>suivi de vol stratosphérique OMEN</strong>.</p>
        <p>Naviguez avec <strong>Suivant →</strong> et <strong>← Précédent</strong>,
        ou quittez à tout moment avec la croix ×.</p>
        <p>À l'issue de la visite, les données de démonstration resteront
        disponibles pour que vous puissiez explorer l'interface librement.</p>
      `,
      buttons: [nextBtn],
    },

    // ── STEP 1 — Selection button ────────────────────────────────────────────
    {
      id: 'tour-step-1',
      attachTo: { element: '[data-tour="tour-flight-selector-button"]', on: 'auto' },
      title: 'Sélection des vols',
      text: 'Commencez par ouvrir le panneau de sélection pour choisir les vols à suivre.<br>Cliquez sur ce bouton pour afficher la liste des vols disponibles.',
      buttons: [prevBtn, nextBtn],
      when: {
        show(): void {
          inject();
          if (!store().isPanelOpen) store().setPanelOpen(true);
        },
      },
    },

    // ── STEP 2 — Selection modal ─────────────────────────────────────────────
    {
      id: 'tour-step-2',
      attachTo: { element: '[data-tour="tour-flight-list-panel"]', on: 'auto' },
      title: 'Panneau de sélection',
      text: 'Les vols sont regroupés par séparateur (IMEI) puis triés du plus récent au plus ancien.<br>Vous pouvez sélectionner jusqu\'à <strong>5 vols</strong> simultanément.<br>Pour cette démonstration, deux vols ont été chargés : <strong>Vol #1002 (SEP-002, terminé)</strong> et <strong>Vol #1001 (SEP-001, en vol)</strong>.',
      buttons: [prevBtn, nextBtn],
      canClickTarget: false,
      beforeShowPromise(): Promise<void> {
        inject();
        return openModalIfNeeded('tour-flight-list-panel', 'tour-flight-selector-button', 500);
      },
      when: {
        hide(): void { closeSelectionModal(); },
      },
    },

    // ── STEP 3 — Map controls (zoom +, zoom –, compass merged) ───────────────
    {
      id: 'map-controls',
      attachTo: { element: '.maplibregl-ctrl-group', on: 'right' },
      title: 'Contrôles de la carte',
      text: `
        <p><strong>+</strong> &nbsp;Augmente le niveau de zoom d'un cran.</p>
        <p><strong>−</strong> &nbsp;Réduit le niveau de zoom pour une vue plus large.</p>
        <p><strong>▲</strong> &nbsp;Compas : affiche l'orientation de la carte.
        Cliquez pour réorienter vers le nord.</p>
      `,
      buttons: [prevBtn, nextBtn],
    },

    // ── STEP 4 — Fit all ─────────────────────────────────────────────────────
    {
      id: 'tour-step-6',
      attachTo: { element: '[data-tour="tour-map-fit-all"]', on: 'auto' },
      title: 'Afficher tous les éléments',
      text: 'Centre et ajuste automatiquement le zoom pour afficher l\'ensemble des trajectoires et positions visibles.',
      buttons: [prevBtn, nextBtn],
      when: {
        show(): void {
          document.querySelector<HTMLButtonElement>('[data-tour="tour-map-fit-all"]')?.click();
        },
      },
    },

    // ── STEP 5 — Basemap selector ────────────────────────────────────────────
    {
      id: 'tour-step-7',
      attachTo: { element: '[data-tour="tour-map-basemap"]', on: 'auto' },
      title: 'Fond de carte',
      text: 'Changez le fond de carte selon vos préférences : vue satellite ou carte routière.',
      buttons: [prevBtn, nextBtn],
    },

    // ── STEP 6 — Flight tabs ─────────────────────────────────────────────────
    {
      id: 'tour-step-8',
      attachTo: { element: '[data-tour="tour-flight-tabs"]', on: 'auto' },
      title: 'Onglets de vols',
      text: 'Chaque vol sélectionné possède son propre onglet. Cliquez sur un onglet pour accéder aux informations de ce vol.<br>La <strong>croix ×</strong> permet de fermer un vol et de retirer son suivi.<br>Nous allons fermer le Vol #1001 pour illustrer cette action.',
      buttons: [prevBtn, nextBtn],
      when: {
        show(): void { store().setActivePanelFlight(TOUR_FLIGHT_ENDED_ID); },
        hide(): void {
          store().deselectFlight(TOUR_FLIGHT_ACTIVE_ID);
          store().setActivePanelFlight(TOUR_FLIGHT_ENDED_ID);
        },
      },
    },

    // ── STEP 7 — Flight header ───────────────────────────────────────────────
    {
      id: 'tour-step-9',
      attachTo: { element: '[data-tour="tour-flight-header"]', on: 'auto' },
      title: 'En-tête du vol',
      text: 'Affiche le nom du séparateur, le numéro du vol et son statut (<strong>EN VOL</strong> ou <strong>VOL TERMINÉ</strong>).<br>Le bouton 👁 permet de masquer temporairement la trajectoire et les prédictions de ce vol sur la carte, sans le désélectionner.',
      buttons: [prevBtn, nextBtn],
      when: {
        show(): void {
          const nextBtnEl = document.querySelector<HTMLButtonElement>(
            '.shepherd-button:not(.shepherd-button-secondary)',
          );
          if (nextBtnEl) nextBtnEl.disabled = true;

          const flightId = TOUR_FLIGHT_ENDED_ID;
          setTimeout(() => {
            store().setFlightPref(flightId, 'isVisible', false);
            setTimeout(() => {
              store().setFlightPref(flightId, 'isVisible', true);
              if (nextBtnEl) nextBtnEl.disabled = false;
            }, 1200);
          }, 800);
        },
      },
    },

    // ── STEP 8 — Telemetry data panel ────────────────────────────────────────
    {
      id: 'tour-step-10',
      attachTo: { element: '[data-tour="tour-frame-panel"]', on: 'auto' },
      title: 'Données de trame',
      text: 'Affiche les paramètres de la trame sélectionnée : position GPS, altitude, vitesse, température, pression, batterie, satellites, timer et la charge utile.<br>L\'indicateur <strong>Position historique</strong> (en jaune) signale que les données affichées correspondent à un point passé de la trajectoire.<br>Les points de la trajectoire sont <strong>cliquables</strong> : cliquez sur un point pour afficher ses données dans ce panneau.',
      buttons: [prevBtn, nextBtn],
    },

    // ── STEP 9 — Back to last position button ─────────────────────────────────
    // Fix 5: uses beforeShowPromise to set historical index before Shepherd
    // attaches the dialog — ensures the "Revenir" button is in the DOM first.
    {
      id: 'tour-step-11',
      attachTo: { element: '[data-tour="tour-back-button"]', on: 'auto' },
      title: 'Revenir à la dernière position',
      text: 'Lorsqu\'un point historique est sélectionné, ce bouton recentre les données sur la <strong>dernière trame reçue</strong> du vol.',
      buttons: [prevBtn, nextBtn],
      beforeShowPromise(): Promise<void> {
        return new Promise((resolve) => {
          store().setSelectedMessageIndex(TOUR_FLIGHT_ENDED_ID, 8);
          // Wait for React to re-render and insert the "Revenir" button before
          // Shepherd tries to attach the dialog to it.
          setTimeout(resolve, 120);
        });
      },
      when: {
        hide(): void { store().setSelectedMessageIndex(TOUR_FLIGHT_ENDED_ID, null); },
      },
    },

    // ── STEP 10 — Zoom buttons ───────────────────────────────────────────────
    {
      id: 'tour-step-12',
      attachTo: { element: '[data-tour="tour-zoom-buttons"]', on: 'auto' },
      title: 'Navigation rapide',
      text: 'Quatre boutons de centrage de la carte :<br>- <strong>Ballon</strong> — zoom sur la dernière position du ballon<br>- <strong>Trajectoire</strong> — ajuste le zoom pour afficher toute la trajectoire du vol<br>- <strong>Préd. norm.</strong> — centre sur la zone de prédiction d\'atterrissage normale<br>- <strong>Préd. inst.</strong> — centre sur la zone de prédiction instantanée',
      buttons: [prevBtn, nextBtn],
    },

    // ── STEP 11 — Teams section ──────────────────────────────────────────────
    {
      id: 'tour-step-13',
      attachTo: { element: '[data-tour="tour-teams-button"]', on: 'auto' },
      title: 'Suivi des équipes',
      text: 'Lorsqu\'il est activé, affiche sur la carte la <strong>dernière position connue</strong> de chaque équipe partageant sa localisation depuis l\'application mobile OMEN, pendant qu\'elles suivent ce vol.',
      buttons: [prevBtn, nextBtn],
    },

    // ── STEP 12 — Predictions section ────────────────────────────────────────
    {
      id: 'tour-step-14',
      attachTo: { element: '[data-tour="tour-predictions-section"]', on: 'auto' },
      title: 'Prédictions d\'atterrissage',
      text: 'Deux types de prédictions sont disponibles :<br>- <strong>Normale</strong> — prédiction calculée en supposant que le ballon monte jusqu\'à l\'<strong>altitude de séparation programmée</strong> avant de larguer la charge utile.<br>- <strong>Instantanée</strong> — prédiction calculée en supposant une séparation immédiate à l\'<strong>altitude de la trame en cours</strong>, utile pour évaluer un atterrissage d\'urgence.',
      buttons: [prevBtn, nextBtn],
      when: {
        show(): void {
          store().setFlightPref(TOUR_FLIGHT_ENDED_ID, 'showNormal', true);
          store().setFlightPref(TOUR_FLIGHT_ENDED_ID, 'showInstant', true);
        },
      },
    },

    // ── STEP 13 — Prediction landing card ────────────────────────────────────
    {
      id: 'tour-step-15',
      attachTo: { element: '[data-tour="tour-prediction-landing-card"]', on: 'auto' },
      title: 'Encart d\'atterrissage',
      text: 'Affiche les détails de la prédiction sélectionnée : coordonnées GPS, heure d\'atterrissage estimée et localité la plus proche.<br>L\'icône 🔗 ouvre directement <strong>Google Maps</strong> centré sur ce point pour faciliter la navigation vers la zone d\'atterrissage.',
      buttons: [prevBtn, nextBtn],
    },

    // ── STEP 14 — Settings button transition ─────────────────────────────────
    {
      id: 'settings-open',
      attachTo: { element: '[data-tour="tour-settings-open-button"]', on: 'bottom' },
      title: 'Paramètres',
      text: 'Accédez aux paramètres de l\'application en cliquant sur ce bouton.<br>Nous allons maintenant explorer les options de personnalisation disponibles.',
      buttons: [prevBtn, nextBtn],
      beforeShowPromise(): Promise<void> {
        closeSettingsModal();
        return Promise.resolve();
      },
    },

    // ── STEP 15 — Settings: colors ────────────────────────────────────────────
    {
      id: 'tour-step-16',
      attachTo: { element: '[data-tour="tour-settings-colors"]', on: 'auto' },
      title: 'Couleurs des vols',
      text: 'Personnalisez la couleur de chaque vol sur la carte pour les distinguer facilement lors du suivi simultané de plusieurs ballons.',
      buttons: [prevBtn, nextBtn],
      beforeShowPromise(): Promise<void> {
        return openModalIfNeeded('tour-settings-colors', 'tour-settings-open-button', 200);
      },
    },

    // ── STEP 16 — Settings: default predictions ───────────────────────────────
    {
      id: 'tour-step-17',
      attachTo: { element: '[data-tour="tour-settings-predictions"]', on: 'auto' },
      title: 'Prédictions par défaut',
      text: 'Définissez quels types de prédictions sont affichés automatiquement à l\'ouverture d\'un vol : normale, instantanée, ou les deux.',
      buttons: [prevBtn, nextBtn],
    },

    // ── STEP 17 — Settings: basemap ───────────────────────────────────────────
    {
      id: 'tour-step-18',
      attachTo: { element: '[data-tour="tour-settings-basemap"]', on: 'auto' },
      title: 'Fond de carte par défaut',
      text: 'Choisissez le fond de carte appliqué au démarrage de l\'application.',
      buttons: [prevBtn, nextBtn],
    },

    // ── STEP 18 — Settings: update interval ──────────────────────────────────
    {
      id: 'tour-step-19',
      attachTo: { element: '[data-tour="tour-settings-update-interval"]', on: 'auto' },
      title: 'Intervalle de mise à jour',
      text: 'Fréquence à laquelle l\'application interroge le serveur pour récupérer les nouvelles trames et prédictions. Un intervalle court donne des données plus fraîches mais sollicite davantage le serveur.',
      buttons: [prevBtn, nextBtn],
    },

    // ── STEP 19 — Settings: flight filter ────────────────────────────────────
    {
      id: 'tour-step-20',
      attachTo: { element: '[data-tour="tour-settings-flight-filter"]', on: 'auto' },
      title: 'Filtre de vols par défaut',
      text: 'Détermine quels vols sont affichés dans le panneau de sélection à l\'ouverture : vols actifs uniquement, tous les vols récents, ou sans filtre.',
      buttons: [prevBtn, nextBtn],
    },

    // ── STEP 20 — Settings: UI scale ──────────────────────────────────────────
    // Fix 6: closeSettingsModal() uses data-tour="tour-settings-close-button" (the ✕
    // button in SettingsModal) — more reliable than clicking the overlay (which
    // would require multi-level DOM traversal from the section element).
    {
      id: 'tour-step-21',
      attachTo: { element: '[data-tour="tour-settings-ui-scale"]', on: 'auto' },
      title: 'Mise à l\'échelle de l\'interface',
      text: 'Ajustez la taille du texte et la largeur du panneau de vol pour adapter l\'interface à votre écran et vos préférences de lisibilité.',
      buttons: [prevBtn, nextBtn],
      when: {
        hide(): void { closeSettingsModal(); },
      },
    },

    // ── STEP 21 — Final (centered, no attachment) ─────────────────────────────
    {
      id: 'tour-step-22',
      title: 'Vous êtes prêt !',
      text: 'Vous connaissez maintenant les principales fonctionnalités d\'OMEN Web Tracker.<br><br>Bonne chasse au ballon ! 🎈',
      buttons: [prevBtn, terminerBtn],
    },
  ];
}
